// import ReactMarkdown from 'react-markdown';
import { css } from 'styled-components';

import { font } from 'atoms/typography/typography';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Iframe from 'quarks/Iframe';
import Image from 'quarks/Image';
import Text from 'quarks/Text';
import type { BasicProps } from 'quarks/interpolations/basic';

import Button, { ButtonProps } from 'molecules/Button/Button';

import richTextParser from 'utils/richTextParser';

import type { IComponentHeadingRecord } from 'graphql/types';
import type { FC } from 'react';

type TextStyles = keyof typeof font.display;

interface HeadingProps extends BasicProps, IComponentHeadingRecord {
  backgroundIsDark: boolean;
  inContent?: boolean;
}

const ComponentHeading: FC<HeadingProps> = ({
  backgroundIsDark,
  eyebrow,
  headline,
  headlineSizeDesktop,
  headlineSizeMobile,
  bodyCopy,
  buttons,
  variant,
  codeSnippet,
  externalVideo,
  logomark,
  featuredMedia,
  inContent,
}) => (
  <Flex
    flexDirection="column"
    flexWrap="nowrap"
    textAlign={variant === 'split' ? 'left' : 'center'}
    alignItems="center"
    marginX="auto"
    gap={inContent ? '0' : '16px'}
    md={{
      gap: inContent ? '0' : '24px',
      flexDirection: 'column',
      maxWidth: '880px',
    }}
    textColor={backgroundIsDark ? 'common-white' : 'storm-900'}
  >
    <Flex flexDirection="column" gap="16px">
      {logomark && (
        <Text marginBottom={16}>
          <img src={logomark.url} alt="startree logo" />
        </Text>
      )}
      {eyebrow && (
        <Text textTransform="uppercase" textStyle="sm" fontWeight="semiBold">
          {eyebrow}
        </Text>
      )}
      {headline && (
        <Heading
          as="h2"
          textStyle={(headlineSizeMobile as TextStyles) || 'md'}
          md={{ textStyle: (headlineSizeDesktop as TextStyles) || 'lg', fontWeight: 'bold' }}
          fontWeight="bold"
        >
          {headline}
        </Heading>
      )}

      {bodyCopy &&
        bodyCopy.value &&
        bodyCopy.value.document &&
        bodyCopy.value.document.children &&
        bodyCopy.value.document.children.some(
          (child: { children: { type: string; value: string }[] }) =>
            child.children && child.children.some(innerChild => innerChild.value),
        ) && (
          <Flex
            fontSize="textLg"
            md={{ fontSize: 'textXl', minWidth: '880px' }}
            flexDirection="column"
            flexWrap="nowrap"
            height="auto"
            textColor={backgroundIsDark ? 'storm-100' : 'wave-800'}
            css={css`
              p + p {
                margin-top: 1.5rem;
              }
              a span {
                font-weight: bold;
              }
              a:hover span {
                text-decoration: none;
              }
            `}
          >
            {richTextParser(bodyCopy as any, backgroundIsDark, true)}
          </Flex>
        )}
    </Flex>
    {featuredMedia && featuredMedia.length > 0 && (
      <Image
        maxWidth={variant === 'centered' ? '100%' : '570px'}
        width="100%"
        marginX="auto"
        marginY={24}
        src={featuredMedia[0].url}
        alt={featuredMedia[0].title as string}
        responsiveImage={featuredMedia[0].responsiveImage as any}
      />
    )}
    {(buttons || codeSnippet || externalVideo) && (
      <Flex
        flexDirection="column"
        gap={externalVideo ? '40px' : '16px'}
        sm={{
          gap: externalVideo ? '56px' : '16px',
        }}
        md={{
          gap: externalVideo ? '72px' : '16px',
        }}
        width="100%"
      >
        {buttons && buttons.length > 0 && (
          <Flex flexDirection="row" flexWrap="wrap" gap="16px" justifyContent="center">
            {buttons.map(button => (
              <Button
                key={button.id}
                href={button?.link || ''}
                color={(button.variant as ButtonProps['color']) || 'primary'}
                size={(button.size as ButtonProps['size']) || 'md'}
                startIcon={button.startIcon}
                endIcon={button.endIcon}
              >
                {button.label}
              </Button>
            ))}
          </Flex>
        )}
        {codeSnippet && richTextParser(codeSnippet.code as any, backgroundIsDark)}
        {externalVideo && (
          <Container
            paddingBottomForce={`${
              externalVideo.width && externalVideo.width !== 0 && externalVideo.height !== 0
                ? (externalVideo.height / externalVideo.width) * 100
                : 60
            }%`}
            position="relative"
          >
            <Iframe
              src={`https://www.youtube.com/embed/${externalVideo.providerUid}`}
              title={externalVideo.title}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              scrolling="no"
              loading="lazy"
              position="absolute"
              left={0}
              top={0}
            />
          </Container>
        )}
      </Flex>
    )}
  </Flex>
);

export default ComponentHeading;
